import { useEffect, useRef, useState } from 'react';
import { get, isEqual } from 'lodash';

import {
  getAppTitle,
  getExpander,
  getModeSelect,
  getOffsetPicker,
  getPrimaryAsset,
  getSecondaryAsset,
} from '../utils';
import { LARGE_SIZE_LAYOUT, MEDIUM_SIZE_LAYOUT, SMALL_SIZE_LAYOUT } from '../constants';

// NOTE: Determine layout according to the width of elements
export function useFilterLayout(components, componentsWidth, appWidth) {
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    if (!appWidth || !componentsWidth) {
      return;
    }

    const titleComponent = getAppTitle(components);
    const expanderComponent = getExpander(components);
    const offsetPickerComponent = getOffsetPicker(components);
    const secondaryAssetComponent = getSecondaryAsset(components);
    const modeSelectComponent = getModeSelect(components);
    const primaryAssetComponent = getPrimaryAsset(components);

    const titleWidth = componentsWidth[get(titleComponent, 'key')] || 0;
    const expanderWidth = componentsWidth[get(expanderComponent, 'key')] || 0;
    const offsetPickerWidth = componentsWidth[get(offsetPickerComponent, 'key')] || 0;
    const secondaryAssetWidth = componentsWidth[get(secondaryAssetComponent, 'key')] || 0;
    const modeSelectWidth = componentsWidth[get(modeSelectComponent, 'key')] || 0;
    const primaryAssetWidth = componentsWidth[get(primaryAssetComponent, 'key')] || 0;

    const extraWidth =
      expanderWidth + offsetPickerWidth + secondaryAssetWidth + modeSelectWidth + primaryAssetWidth;

    if (titleWidth + extraWidth <= appWidth) {
      setLayout(LARGE_SIZE_LAYOUT);
    } else if (titleWidth + primaryAssetWidth <= appWidth) {
      setLayout(MEDIUM_SIZE_LAYOUT);
    } else {
      setLayout(SMALL_SIZE_LAYOUT);
    }
  }, [componentsWidth, appWidth]);

  return layout;
}

// NOTE: Calculate the width of child components here
export function useComonentsWidth(components) {
  const hiddenElementsRef = useRef();
  const [widthDict, setWidthDict] = useState(null);
  const prevComponentsRef = useRef({});

  const validComponents = components.filter(component => !!component);
  const hiddenElements = (
    <div
      ref={hiddenElementsRef}
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
      {validComponents.map((component, index) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} data-key={component?.key} style={{ position: 'absolute' }}>
            {component}
          </div>
        );
      })}
    </div>
  );

  // NOTE: Calcualte width of each components
  useEffect(() => {
    // NOTE: Don't calcuate width again
    if (isEqual(validComponents, prevComponentsRef.current)) {
      return;
    }

    // NOTE: There's data loading in offset picker
    const offsetPickerComponent = getOffsetPicker(components);
    const offsetPickerKey = get(offsetPickerComponent, 'key');

    const nodesArray = Array.from(hiddenElementsRef.current?.childNodes);
    const sizeMap = nodesArray.reduce((acc, node) => {
      const key = node?.getAttribute?.('data-key');
      const value = key === offsetPickerKey ? 36 : node?.getBoundingClientRect().width;

      return {
        ...acc,
        [key]: value,
      };
    }, {});

    if (Object.values(sizeMap).length === validComponents.length) {
      if (!isEqual(widthDict, sizeMap)) {
        setWidthDict(sizeMap);
      }
    }

    prevComponentsRef.current = validComponents;
  });

  return { componentsWidth: widthDict, hiddenElements };
}
